'use client';

import { useCallback, useMemo } from 'react';

import classnames from 'classnames';

import type { BaseComponentProps } from '../../base-component.types';
import { SelectDropdown } from '../../forms';

import styles from './TimePicker.module.scss';

type ContainerProps = BaseComponentProps & {
  date: Date;
  minDate: Date;
  maxDate: Date;
  onChange: (date: Date) => void;
};

// Helper function to check if two dates are on the same day
const isSameDay = (date1: Date, date2: Date): boolean => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

// Helper function to create a new date with updated time
const updateTime = (date: Date, hours: number, minutes: number): Date => {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    hours,
    minutes,
    date.getSeconds(),
  );
};

function TimePicker({
  style,
  className,
  date,
  minDate,
  maxDate,
  onChange,
  testId,
}: ContainerProps) {
  const hourOptions = useMemo(() => {
    const isSameDayRange = isSameDay(minDate, maxDate);
    const currentHour = date.getHours();

    return Array.from({ length: 24 }, (_, value) => ({
      value: value.toString(),
      label: value < 10 ? `0${value}` : value.toString(),
      selected: value === currentHour,
      disabled:
        isSameDayRange &&
        (value < minDate.getHours() || value > maxDate.getHours()),
    }));
  }, [date, minDate, maxDate]);

  const minuteOptions = useMemo(() => {
    const currentMinute = date.getMinutes();
    const isMaxDateSameDay = isSameDay(date, maxDate);
    const isMinDateSameDay = isSameDay(date, minDate);
    const currentHour = date.getHours();

    return Array.from({ length: 60 }, (_, value) => {
      const isDisabledMax =
        isMaxDateSameDay &&
        currentHour === maxDate.getHours() &&
        value > maxDate.getMinutes();
      const isDisabledMin =
        isMinDateSameDay &&
        currentHour === minDate.getHours() &&
        value < minDate.getMinutes();

      return {
        value: value.toString(),
        label: value < 10 ? `0${value}` : value.toString(),
        selected: value === currentMinute,
        disabled: isDisabledMax || isDisabledMin,
      };
    });
  }, [date, minDate, maxDate]);

  const handleHourChange = useCallback(
    (hourValue: string | number) => {
      const newHour = Number(hourValue);
      const currentMinute = date.getMinutes();
      let newMinute = currentMinute;

      // Adjust minutes if needed when hour changes
      if (isSameDay(date, minDate) && newHour === minDate.getHours()) {
        newMinute = Math.max(currentMinute, minDate.getMinutes());
      }
      if (isSameDay(date, maxDate) && newHour === maxDate.getHours()) {
        newMinute = Math.min(currentMinute, maxDate.getMinutes());
      }

      onChange(updateTime(date, newHour, newMinute));
    },
    [date, minDate, maxDate, onChange],
  );

  const handleMinuteChange = useCallback(
    (minuteValue: string | number) => {
      const newMinute = Number(minuteValue);
      onChange(updateTime(date, date.getHours(), newMinute));
    },
    [date, onChange],
  );

  return (
    <div
      style={style}
      data-testid={testId}
      className={classnames(styles['time-picker'], className)}
    >
      <SelectDropdown
        className={styles['dropdown']}
        onSelect={handleHourChange}
        testId={`${testId}_hour`}
        options={hourOptions}
      />
      <span className={styles['separator']}>:</span>
      <SelectDropdown
        className={styles['dropdown']}
        onSelect={handleMinuteChange}
        testId={`${testId}_minutes`}
        options={minuteOptions}
      />
    </div>
  );
}

export { TimePicker };
